import { loggerFactory, LoggerType } from '@ria-client/logger';
import cookies from 'js-cookie';
import { Kind } from './constants';
import { GraphQLClientError, RequestError, type GraphQLErrorResponse } from './errors';
import { errorFilters } from './errors/filters';
import type { Options, QueryOrMutation, Variables } from './types';
import { getSupergraphUrl } from './utils';

type ClientConfig = {
  /**
   * The URL of the GraphQL API.
   */
  url: string;
  /**
   * Additional headers to send with the request.
   */
  headers?: Record<string, string>;
  /**
   * A logger to use for logging requests and responses.
   * The logger should have a `trace` method for logging requests and responses,
   * and an `error` method for logging errors.
   */
  logger?: LoggerType;
};

/**
 * A simple GraphQL client that sends queries and mutations to a GraphQL API.
 * It uses the Fetch API to make requests.
 */
export class GraphQLClient {
  /**
   * @param config The configuration for the client.
   */
  constructor(private readonly config: ClientConfig) {}

  private async request<Response = unknown, Vars extends Variables = Variables>(
    queryOrMutation: QueryOrMutation,
    variables?: Vars,
    options?: Options,
  ): Promise<Response> {
    const { url, headers = {}, logger } = this.config;
    const nw_request_id = crypto.randomUUID();

    logger?.trace?.({ nw_request_id }, `Making request to ${url}`);

    try {
      let operationName = {};
      if (typeof queryOrMutation !== 'string') {
        for (const definition of queryOrMutation.definitions) {
          if (definition.kind === Kind.OPERATION_DEFINITION) {
            const name = definition?.name?.value;
            if (name) {
              operationName = {
                operationName: name,
              };
            }
          }
        }
      }

      const body = JSON.stringify({
        query:
          typeof queryOrMutation === 'string' ? queryOrMutation : queryOrMutation.loc?.source.body,
        variables,
        ...operationName,
      });

      const response = await fetch(url, {
        method: 'POST',
        body,
        headers: {
          // 'x-request-id' is used for tracing requests in the logs.
          'x-request-id': nw_request_id,
          ...headers,
        },
        credentials: 'include',
        ...options,
      });

      if (!response.ok) {
        throw new RequestError(
          `Status code: ${response.status} ${response.statusText}${await describeBody(response)}`,
          response,
        );
      }

      logger?.trace?.({ nw_request_id }, `Received response with status code: ${response.status}`);

      let responseJson: Response & GraphQLErrorResponse;
      try {
        responseJson = (await response.clone().json()) as Response & GraphQLErrorResponse;
      } catch {
        // An HTML error or challenge page served with 200: name it instead of a bare
        // "Unexpected token '<'" so CI and ISR logs say what stage actually returned.
        throw new RequestError(
          `Non-JSON response, status code: ${response.status}${await describeBody(response)}`,
        );
      }

      if (responseJson.errors) {
        const errors = errorFilters.reduce(
          (acc, filter) => acc.filter(filter),
          responseJson.errors || [],
        );

        if (errors.length > 0) {
          throw new RequestError(
            `GraphQL errors: ${errors.map(err => err.message).join(', ')}`,
            response,
          );
        }
      }

      return responseJson;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        logger?.trace?.({ nw_request_id, error }, 'Request was aborted');

        throw new GraphQLClientError('Request was aborted', {
          cause: error,
        });
      }

      if (error instanceof RequestError && error.name === 'RequestError' && error.response) {
        // Non-2xx bodies are often HTML; a parse failure here must not mask the status error.
        const responseJson = (await error.response
          .clone()
          .json()
          .catch(() => ({}))) as Response & GraphQLErrorResponse;

        const errors = errorFilters.reduce(
          (acc, filter) => acc.filter(filter),
          responseJson.errors || [],
        );

        const flatErrors = errors.map(error => {
          return {
            extensions: error.extensions,
            message: error.message,
          };
        });

        if (flatErrors.length > 0) {
          logger?.error?.(
            { nw_request_id, flatErrors },
            `Request failed with: ${(error as Error).message}`,
          );
        }

        delete error.response;

        throw new GraphQLClientError(`Failed to request GraphQL API. ${(error as Error).message}`, {
          cause: error instanceof Error ? error : undefined,
        });
      }

      logger?.error?.(
        { nw_request_id, error },
        `[Uncaught] Request failed with: ${(error as Error).message}`,
      );

      throw new GraphQLClientError(`Failed to request GraphQL API. ${(error as Error).message}`, {
        cause: error instanceof Error ? error : undefined,
      });
    }
  }

  /**
   * Send a query to the GraphQL API.
   * @param query
   * @param variables
   * @param options Additional options to pass to the Fetch API.
   * @returns A promise that resolves to the response from the API.
   */
  query<Response = unknown, Vars extends Variables = Variables>(
    query: QueryOrMutation,
    variables?: Vars,
    options?: Options,
  ): Promise<Response> {
    return this.request<Response>(query, variables, options);
  }

  /**
   * Send a mutation to the GraphQL API.
   * @param query
   * @param variables
   * @param options Additional options to pass to the Fetch API.
   * @returns A promise that resolves to the response from the API.
   */
  mutation<Response = unknown, Vars extends Variables = Variables>(
    query: QueryOrMutation,
    variables?: Vars,
    options?: Options,
  ): Promise<Response> {
    return this.request<Response>(query, variables, options);
  }
}

/**
 *  Create a new GraphQL client with the given configuration.
 * @param config The configuration for the client.
 * @returns GraphQLClient instance.
 */
export function getGraphQLClient(config?: Partial<ClientConfig>) {
  const url = config?.url ?? getSupergraphUrl();
  const logger =
    config?.logger ??
    loggerFactory({
      component: 'graphql-client',
      path: 'packages/supergraph/graphql-client',
    });

  let csrfToken;
  if (typeof window !== 'undefined') {
    csrfToken = cookies.get('csrf_token');
  }

  const headers: Record<string, string> = {
    'X-Client-Platform': 'web',
    'X-Caller-Client-ID': 'ria-client',
    'GraphQL-Client-Name': 'ria-client',
    ...(csrfToken ? { 'X-Header-CSRF-Token': csrfToken } : {}),
    ...config?.headers,
  };

  if (!headers['content-type'] && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  return new GraphQLClient({
    url,
    headers,
    logger,
  });
}

/** `, content-type: …, title|body: …` for a non-JSON response, read from a clone. */
async function describeBody(response: globalThis.Response) {
  const contentType = response.headers?.get('content-type') ?? 'none';
  if (contentType.includes('json')) return '';
  const text = await response
    .clone()
    .text()
    .catch(() => '');
  const title = /<title[^>]*>([^<]*)<\/title>/i.exec(text)?.[1]?.trim();
  const summary = title ? `title: ${title}` : `body: ${text.replace(/\s+/g, ' ').slice(0, 120)}`;
  return `, content-type: ${contentType}, ${summary}`;
}
